'use client';

import { startTransition, useCallback, useEffect, useMemo, useState } from 'react';
import {
  DeliveryEstimateDetails,
  ProductDetailsProps,
  ShippingMethod,
  Wishlist,
} from '@/components/organisms/product-details/types';
import ProductDetails from '@/components/organisms/product-details';
import useWishlist from '@/lib/hooks/useWishlist';
import useCart from '@/lib/hooks/useCart';
import { useShipAndLanguage } from '@/providers/ship-and-language';
import { ShippingRate } from '@shared/types/cart';
import { Attribute, Product } from '@/types/entity/product';
import { Currency } from '@/types/currency';
import { Variant } from '@shared/types/product';
import { getLocalizationInfo } from '@/project.config';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import { ProductDetailsMapperProps } from '../types';
import usePath from '@/hooks/usePath';
import { useParams, useRouter } from 'next/navigation';
import { sdk } from '@/sdk/CommercetoolsSDK';
import { BUSINESS_UNIT_POSTAL_CODE } from '@/constants/generic';
import { isExclusiveProductEnabled } from '@/utils/feature-flag';
import useFormat from '@/hooks/useFormat';
import { localeMapping } from '@/constants/localeMapping';
import useAccount from '@/lib/hooks/useAccount';

const ProductDetailsMapper = ({ product }: ProductDetailsMapperProps) => {
  const { locale } = useParams();
  const localeString = Array.isArray(locale) ? locale[0] : locale || '';
  const localeKey = localeMapping[localeString];
  const router = useRouter();
  const [currentVariantIndex, setCurrentVariantIndex] = useState<Variant | undefined>();
  const { selectedLocation } = useShipAndLanguage();
  const { selectedBusinessUnit, selectedStore } = useStoreAndBusinessUnits()
  const { addToWishlist: CTAddToWishlist, getWishlists } = useWishlist();
  const { addItem, shippingMethods } = useCart(selectedBusinessUnit?.key, locale === "au" ? "plumbershub-au" : "plumbershub-nz",);
  const { path, absolutePath } = usePath();
  const extensions = sdk.composableCommerce;
  const [itemDoesNotExist, setItemDoesNotExist] = useState(false);
  const [inventoryData, setInventoryData] = useState([]);
  const [postalCode, setPostalCode] = useState<string | null>(null);
  const [deliveryEstimate, setDeliveryEstimate] = useState<DeliveryEstimateDetails>();
  const productSKU = path?.split('/p/')?.[1]?.split('/')?.[0] || '';
  const selectedVariant = product?.variants.filter(item => item.sku === productSKU)
  const [itemPrice, setItemPrice] = useState<number | undefined>();
  const [quantityPrice, setQuantityPrice] = useState('');
  const [quantityPriceLoading, setQuantityPriceLoading] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [loadingDeliverEstimate, setLoadingDeliveryEstimate] = useState(true);
  const [customerQuantityPrice, setCustomerQuantityPrice] = useState<any>();
  const { account } = useAccount();
  const { formatCurrency } = useFormat();
  useEffect(() => {
    const accountPostalCode = account?.postalCode;
    const localStoragePostalCode = localStorage.getItem('--postal-code');

    const isValid = (pc: any) => pc && pc.length >= 4 && pc.length <= 5 && /^\d+$/.test(pc);

    if (isValid(localStoragePostalCode)) {
      setPostalCode(localStoragePostalCode);
    } else if (isValid(accountPostalCode)) {
      setPostalCode(accountPostalCode);
    } else {
      const defaultPostcode = locale === 'au' ? '2000' : '1026';
      setPostalCode(defaultPostcode);
    }
  }, [account, locale]);


  const itemCode = currentVariantIndex?.attributes?.['item-code'];
  const filterByLocale = useCallback(
    (refs: string[]): string[] => {
      const countrySuffix = localeString.toUpperCase(); // 'AU' or 'NZ'
      return refs
        .filter((ref: string) => {
          const pipeIndex = ref.indexOf('|');
          if (pipeIndex === -1) return true; // no suffix — include as-is (backwards compat)
          const suffix = ref.slice(pipeIndex + 1); // e.g. 'AU01', 'AU', 'NZ01', 'NZ'
          return suffix.toUpperCase().startsWith(countrySuffix);
        })
        .map((ref: string) => ref.split('|')[0]);
    },
    [localeString],
  );

  const componentsReferences = useMemo(
    () =>
      filterByLocale(
        currentVariantIndex?.attributes?.['item-attributes']?.find(
          (attr: { name: string }) => attr.name === 'bundle-references',
        )?.value ?? [],
      ),
    [currentVariantIndex, filterByLocale],
  );
  
  const sparePartsReferences = useMemo(
    () =>
      filterByLocale(
        currentVariantIndex?.attributes?.['item-attributes']?.find(
          (attr: { name: string }) => attr.name === 'spare-references',
        )?.value ?? [],
      ),
    [currentVariantIndex, filterByLocale],
  );

  const toiletSeatPancompatibilityReferences = useMemo(
    () =>
      filterByLocale(
        currentVariantIndex?.attributes?.['item-attributes']?.find(
          (attr: { name: string }) => attr.name === 'toilet-seatpan-recommnded-references',
        )?.value ?? [],
      ),
    [currentVariantIndex, filterByLocale],
  );

  const toiletSeatPanSuitableReferences = useMemo(
    () =>
      filterByLocale(
        currentVariantIndex?.attributes?.['item-attributes']?.find(
          (attr: { name: string }) => attr.name === 'toilet-seatpan-suitable-references',
        )?.value ?? [],
      ),
    [currentVariantIndex, filterByLocale],
  );


  const [componentsProducts, setComponentsProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isBundleLoading, setIsBundleLoading] = useState(false);
  const [isPriceLoading, setIsPriceLoading] = useState(false);
  const isBundleProduct = sparePartsReferences?.length > 0 || componentsReferences?.length > 0 || toiletSeatPanSuitableReferences?.length > 0 || toiletSeatPancompatibilityReferences?.length > 0;
  const COUNTRY: any = {
    au: 'AU',
    nz: 'NZ',
  };
  const store = typeof window !== 'undefined' ? localStorage.getItem('locale') : 'au';

  useEffect(() => {
    if (!product) return;
    const currentVariantPath = absolutePath.match(/\/p\/(\d+)\//)?.[1];
    const currentVariantSKU = currentVariantPath?.split('?')[0];
    const currentVariantIndex = product.variants.findIndex(({ sku }) => sku === currentVariantSKU);
    const safeStore = store || 'au';
    const exclusiveProductIsEnabled = isExclusiveProductEnabled();
    let exclusiveVariant = product?.variants[currentVariantIndex]?.attributes?.[
      `exclusive-product-${COUNTRY[safeStore] || 'au'}`
    ]?.map((v: any) => v?.toLowerCase());

    if (exclusiveProductIsEnabled && selectedBusinessUnit && !!selectedBusinessUnit?.company_chain && !!exclusiveVariant?.length) {
      const isNullPresent = exclusiveVariant.some((v: any) => v === 'null');
      if (
        !isNullPresent &&
        !exclusiveVariant.some((v: any) => v.toLowerCase().includes(selectedBusinessUnit?.company_chain?.toLowerCase()))
      ) {
        router.push('/404');
      }
    }
    if (exclusiveProductIsEnabled && selectedBusinessUnit && !selectedBusinessUnit?.company_chain) {
      if (!!exclusiveVariant?.length) {
        const isNullPresent = exclusiveVariant.some((v: any) => v === 'null');
        if (!isNullPresent) {
          router.push('/404');
        }
      }
    }

    setCurrentVariantIndex(product.variants[currentVariantIndex]);
    const clickedVariant = product.variants[currentVariantIndex];
    const clickedVariantSKU = clickedVariant?.sku ?? '';

    // Only update URL if the SKU is different from what's currently in the URL
    if (absolutePath && clickedVariantSKU && !absolutePath.includes(`/p/${clickedVariantSKU}/`)) {
      const urlParts = absolutePath.split('/');
      const productIndex = urlParts.findIndex((part) => part === 'p');
      if (productIndex !== -1 && productIndex + 1 < urlParts.length) {
        urlParts[productIndex + 1] = clickedVariantSKU;
        const newUrl = urlParts.join('/');
        if (typeof window !== 'undefined') {
          window.history.replaceState({}, '', newUrl);
        }
      }
    }

    // Recently Viewed Products
    let storedProducts = localStorage.getItem('recent-products');

    let recentProducts = [];
    try {
      recentProducts = storedProducts ? JSON.parse(storedProducts) : [];
    } catch (error) {
      console.log("Error parsing recent-products from localStorage", error);
      recentProducts = [];
    }

    const businessUnitKey = selectedBusinessUnit?.key;
    const existingProduct: any = recentProducts?.find((product: any) => product?.business_unit === businessUnitKey);


    if (!!existingProduct) {
      if (!existingProduct?.products?.includes(currentVariantSKU)) {
        existingProduct.products.push(currentVariantSKU);
      }
    } else {
      recentProducts.push({
        business_unit: businessUnitKey,
        products: [currentVariantSKU],
      });
    }
    localStorage.setItem('recent-products', JSON.stringify(recentProducts));
  }, [selectedBusinessUnit, product, store, absolutePath]);

  const itemCodes = useMemo(
    () => [ ...componentsReferences, ...sparePartsReferences, ...toiletSeatPanSuitableReferences, ...toiletSeatPancompatibilityReferences],
    [componentsReferences, sparePartsReferences, toiletSeatPanSuitableReferences, toiletSeatPancompatibilityReferences],
  );


  const fetchBundleData = useCallback(
    async (productCodes: any) => {
      if (!productCodes || productCodes.length === 0 || !isBundleProduct) return;
      let storeKey: any = '';
      if (typeof window !== 'undefined') {
        storeKey = localStorage.getItem('store');
      }
      const requestData = {
        variant: productCodes,
        storeKey,
      };
      try {
        setIsBundleLoading(true);
        const responseBody = await extensions.product.bundleProducts(JSON.stringify(requestData));
        setComponentsProducts(responseBody?.data?.data);
      } catch (error) {
        console.error('Bundle Data Fetch Error:', error);
      } finally {
        setIsBundleLoading(false);
      }
    },
    [itemCode, isBundleProduct],
  );

  useEffect(() => {
    if (itemCode) {
      fetchBundleData([...componentsReferences, ...sparePartsReferences, ...toiletSeatPanSuitableReferences, ...toiletSeatPancompatibilityReferences]);
    }
  }, [itemCode]);

  const fetchInventoryData = useCallback(async (productCodes: any) => {
    if (!productCodes) return;
    const requestData = {
      _contract: {
        legalentity: locale === 'au' ? 'AU01' : 'NZ01',
        itemcodes: productCodes,
      },
    };
    try {
      const response = await extensions.custom.productInventory(JSON.stringify(requestData));

      if (response?.data?.data?.data?.length === 0) {
        const error = response?.data.data?.message;
        const string = "doesn't exist";
        const result = error.indexOf(string);
        if (result >= 0) {
          setItemDoesNotExist(true);
        } else {
          setItemDoesNotExist(false);
        }
      } else {
        setInventoryData(response?.data?.data?.data);
      }
    } catch (error) {
      console.error('Error fetching inventory data:', error);
    }
  }, [itemCodes]);

  useEffect(() => {
    if (itemCode) {
      fetchInventoryData([...componentsReferences, ...sparePartsReferences, ...toiletSeatPanSuitableReferences, ...toiletSeatPancompatibilityReferences]);
    }
  }, [itemCode]);
  

  const deliveryEstimateData = useCallback(async () => {
    const requestData = {
      "_request": {
        "LegalEntity": locale === 'au' ? 'AU01' : 'NZ01',
        "PONO": postalCode,
        "CustNo": "",
        "ItemCode": itemCode,
        "Qty": "1"
      }
    };
    try {
      setIsLoading(true);
      const response = await extensions.custom.deliveryEstimateData(JSON.stringify(requestData));
      setDeliveryEstimate(response?.data?.data);
    } catch (error) {
      console.error('Error fetching inventory data:', error);
    } finally {
      setIsLoading(false);
      setLoadingDeliveryEstimate(false);
    }
  }, [postalCode, itemCode]);


  useEffect(() => {
    if (!itemCode) return;

    (async () => {
      try {
        setLoadingDeliveryEstimate(true);
        await Promise.all([deliveryEstimateData()]);
      } catch (error) {
        setLoadingDeliveryEstimate(false);
        console.error('Error fetching data:', error);
      }
    })();

  }, [itemCode, deliveryEstimateData]);

  const addToCart = useCallback(
    async (count: number) => {
      return await addItem([{ sku: currentVariantIndex?.sku ?? '', count }]);
    },
    [addItem, currentVariantIndex],
  );

  const getShippingPrice = useCallback(
    (rates: Array<ShippingRate>): number => {
      const { countryCode } = getLocalizationInfo(selectedLocation?.value ?? 'sv');

      const currentRate = rates?.find((rate) => rate.name === countryCode);
      return (currentRate?.price?.centAmount ?? 1000) / 100;
    },
    [selectedLocation],
  );

  const getShippingMethods = useCallback((): Array<ShippingMethod> => {
    const mappedShippingMethods =
      (shippingMethods?.map((method: any) => ({
        label: method.name ?? '',
        price: getShippingPrice(method.rates ?? []),
        estimatedDeliveryDays: parseInt(method.description ?? '3'),
      })) as Array<ShippingMethod>) ?? [];

    return mappedShippingMethods;
  }, [getShippingPrice, shippingMethods]);

  const onChangeVariant = useCallback(
    (variant: 'color' | 'specs', value: string) => {
      const updatedVariantIdx = product.variants.findIndex((v) => v.attributes?.[variant].key === value);
      // setCurrentVariantIndex(updatedVariantIdx ?? 0);
    },
    [product.variants],
  );
  const handleColorClick = useCallback(
    (index: number) => {
      const clickedVariant = product.variants[index];
      const clickedVariantSKU = clickedVariant.sku ?? '';

      setCurrentVariantIndex(clickedVariant);

      // Only update URL if the SKU is different from what's currently in the URL
      if (absolutePath && clickedVariantSKU && !absolutePath.includes(`/p/${clickedVariantSKU}/`)) {
        const urlParts = absolutePath.split('/');
        const productIndex = urlParts.findIndex((part) => part === 'p');
        if (productIndex !== -1 && productIndex + 1 < urlParts.length) {
          urlParts[productIndex + 1] = clickedVariantSKU;
          const newUrl = urlParts.join('/');
          if (typeof window !== 'undefined') {
            window.history.pushState({}, '', newUrl);
          }
        }
      }
    },
    [product.variants, absolutePath],
  );

  const addToWishlist = useCallback(
    async (wishlistId: string) => {
      return await CTAddToWishlist(wishlistId, {
        sku: currentVariantIndex?.sku ?? '',
      }).then((res: any) => {
        return {
          label: res.data?.name,
          id: res.data?.wishlistId,
        } as Wishlist;
      });
    },
    [CTAddToWishlist, currentVariantIndex],
  );

  const mapColorAttributes = useCallback((variant: Variant): Attribute => {
    return {
      label: variant.attributes?.color.label,
      value: variant.attributes?.color.key,
    };
  }, []);

  useEffect(() => {
    if (currentVariantIndex?.attributes?.["builder-range"] !== undefined) {
    }
  }, [currentVariantIndex]);

  const productDetailsProps: any = useMemo(() => {
    const variant = currentVariantIndex;

    const currentColor: Attribute | undefined = variant?.attributes?.color ? mapColorAttributes(variant) : undefined;
    const productType = variant?.attributes?.['product-types'] === 'Brochures';
    const variantKey = variant?.key

    const colors: Attribute[] = currentColor
      ? product.variants.map((variant) => {
        return mapColorAttributes(variant);
      })
      : [];
    const tradePriceKey = variant && variant?.attributes && variant?.attributes[`trade-price-${localeString.toUpperCase()}`];
    return {
      product: {
        id: product.productId ?? '',
        sku: variant?.sku ?? '',
        name: product.name ?? '',
        currency: (variant?.price?.currencyCode ?? 'USD') as Currency,
        model: variant?.sku ?? '',
        images: variant?.images ?? [],
        price: (variant?.price?.centAmount ?? 1000) / 100,
        tradePrice: tradePriceKey,
        colors,
        slug: product?.slug,
        title: product?.metaTitle,
        metaDescription: product?.metaDescription,
        metaKeywords: product?.metaKeywords,
        discountedPrice: variant?.discountedPrice ? (variant.discountedPrice?.value?.centAmount ?? 1000) / 100 : undefined,
        categories: product.categories?.map((category) => {
          const categoryUrl = typeof category._url === 'string'
            ? category._url
            : (category._url?.[localeKey] ?? category._url?.[localeString] ?? category.slug ?? '');
          return {
            id: category.categoryId ?? '',
            categoryId: category.categoryKey ?? '',
            name: category.name ?? '',
            parentName: category.parentName ?? '',
            path: categoryUrl,
            slug: category.slug ?? '',
            parentKey: category.parentKey ?? '',
          };
        }),
        exclusiveProduct: variant?.attributes?.[`exclusive-product-${locale === 'au' ? 'AU' : 'NZ'}`]?.map((p: any) =>
          p.toLowerCase(),
        ),
        isNew: variant?.attributes?.IsNew,
        runDown: variant?.attributes?.['item-attributes']?.find((attr: { name: string }) => attr.name === 'lifecycle')
          ?.value[localeKey]?.toLowerCase() === 'run down',
        lifecycle: variant?.attributes?.['item-attributes']?.find((attr: { name: string }) => attr.name === 'lifecycle')
          ?.value[localeKey],
        isGSTExempt: variant?.attributes?.['IsGSExempt'],
      },
      inventoryData,
      componentsProducts,
      itemDoesNotExist,
      deliveryEstimate,
      isLoading,
      isBundleLoading,
      isPriceLoading,
      variant,
      variants: product.variants,
      currentColor,
      currentVariantIndex,
      handleColorClick,
      additionalInfo: [],
      shippingMethods: getShippingMethods(),
      addToCart,
      getWishlists,
      addToWishlist,
      onChangeVariant,
      itemPrice,
      productType,
      variantKey,
      setQuantity,
      quantityPrice,
      quantity,
      quantityPriceLoading,
      setPostalCode,
      postalCode,
      loadingDeliverEstimate
    };
  }, [
    product.variants,
    product.productId,
    product.name,
    product.categories,
    product.description,
    currentVariantIndex,
    mapColorAttributes,
    getShippingMethods,
    addToCart,
    getWishlists,
    addToWishlist,
    onChangeVariant,

  ]);

  return <>{currentVariantIndex && <ProductDetails {...productDetailsProps} />}</>;
};

export default ProductDetailsMapper;